import type {
  InstitutionalOwnership,
  QuarterComparison,
  QuarterOverQuarterChange,
} from "../../../SuperInvestors/hooks/useSuperInvestorHoldings";

export interface HoldingPositionRow {
  id: string;
  company: string;
  symbol: string;
  shares: number | null;
  value: number | null;
  weight: number | null;
}

export interface PortfolioMoveRow {
  id: string;
  company: string;
  symbol: string;
  action: "New" | "Closed" | "Added" | "Reduced" | "Unchanged";
  shareChange: number;
  shareChangePercent: number | null;
  previousWeight: number | null;
  currentWeight: number | null;
  magnitude: number;
}

export const getCompanyHoldingRows = (
  holdings: InstitutionalOwnership[],
  totalValue: number,
): HoldingPositionRow[] =>
  holdings.map((holding, index) => ({
    id: `${holding.securityCusip}-${holding.putCallShare}-${index}`,
    company: holding.nameOfIssuer,
    symbol: holding.symbol,
    shares: holding.shares ?? null,
    value: holding.value ?? null,
    weight:
      totalValue > 0 &&
      Number.isFinite(totalValue) &&
      Number.isFinite(holding.value)
        ? (holding.value / totalValue) * 100
        : null,
  }));

export const getPositionRows = (
  comparison: QuarterComparison,
  type: "NEW" | "CLOSED",
): HoldingPositionRow[] =>
  comparison.changes
    .filter((change) => change.positionType === type)
    .map((change, index) => {
      const snapshot =
        type === "NEW" ? change.currentQuarter : change.previousQuarter;
      return {
        id: `${change.securityCusip}-${index}`,
        company: change.nameOfIssuer,
        symbol: change.symbol,
        shares: snapshot?.shares ?? null,
        value: snapshot?.value ?? null,
        weight: snapshot?.portfolioPercentage ?? null,
      };
    });

const getMoveAction = (
  change: QuarterOverQuarterChange,
): PortfolioMoveRow["action"] => {
  if (change.positionType === "NEW") return "New";
  if (change.positionType === "CLOSED") return "Closed";
  if (change.changes.shareChange > 0) return "Added";
  if (change.changes.shareChange < 0) return "Reduced";
  return "Unchanged";
};

export const getPortfolioMoveRows = (
  comparison: QuarterComparison,
): PortfolioMoveRow[] =>
  comparison.changes.map((change, index) => ({
    id: `${change.securityCusip}-${index}`,
    company: change.nameOfIssuer,
    symbol: change.symbol,
    action: getMoveAction(change),
    shareChange: change.changes.shareChange,
    // A new position has no previous share count from which to calculate growth.
    shareChangePercent:
      (change.previousQuarter?.shares ?? 0) > 0
        ? change.changes.shareChangePercent
        : null,
    previousWeight: change.previousQuarter?.portfolioPercentage ?? null,
    currentWeight: change.currentQuarter?.portfolioPercentage ?? null,
    magnitude: Math.abs(change.changes.portfolioPercentageChange),
  }));
